import { ArrowRight, Target, Lightbulb, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const MarketingStrategy = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-yellow-50/30">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Marketing Strategy Services
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Strategic planning and workshops to guide your business growth with clarity and direction.
          </p>
        </div>
      </section>

      {/* 12 Month Marketing Plan */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                12-Month Marketing Plan Workshop
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We have developed a unique process to deliver a 12-month marketing plan that is not only aimed at delivering on your strategic goals but keeps your marketing flexible and evergreen.
                </p>
                <p>
                  It is based on the <span className="font-semibold text-gray-900">SOSTAC model</span> and is combined with a unique bespoke branding and USP workshop so that you never lose sight of who you are as a brand.
                </p>
              </div>
              <div className="mt-8">
                <Button
                  asChild
                  className="bg-[#FBD116] hover:bg-[#E5C015] text-gray-900 font-semibold"
                >
                  <a href="https://calendly.com/bronny-mpremarketing/30min" target="_blank" rel="noopener noreferrer">
                    Learn More
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop"
                alt="Strategic planning"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Target,
                title: '12-Month Marketing Plan Workshop',
                description: 'Comprehensive strategic planning based on the SOSTAC model combined with branding and USP workshops.'
              },
              {
                icon: Lightbulb,
                title: 'DIY Marketing Plan Tool',
                description: 'Download and work through our planning tool yourself with check-in sessions from our marketing experts.'
              },
              {
                icon: TrendingUp,
                title: 'Branding Workshop',
                description: 'Find your voice and gain clarity in who you are, what you do, and how you present yourself to the world.'
              }
            ].map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="border-2 hover:border-[#FBD116] transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 rounded-full bg-[#FBD116]/10 flex items-center justify-center mb-6">
                      <Icon className="w-8 h-8 text-gray-900" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* DIY Tool Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1432888622747-4eb9a8f2c293?w=800&h=600&fit=crop"
                alt="DIY Marketing Tools"
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                12-Month Marketing Plan DIY Tool
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Not every business has money to spend on strategy. They are busy functioning day to day. However, that cycle can be limiting. If you don't look ahead how do you know where you are going?
                </p>
                <p>
                  This tool, you can download and work through by yourself booking in check-in sessions with a Unite Career Marketer.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Branding Workshop Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Branding Workshop
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Finding your voice can be very difficult. This process allows you to keep things simple, and find clarity in who you are, what you do, and how you want to present yourself to the world.
                </p>
                <p>
                  Our workshops help you uncover your unique selling proposition and develop a brand voice that resonates with your target audience.
                </p>
              </div>
              <div className="mt-8">
                <Button
                  asChild
                  className="bg-[#FBD116] hover:bg-[#E5C015] text-gray-900 font-semibold"
                >
                  <a href="https://calendly.com/bronny-mpremarketing/30min" target="_blank" rel="noopener noreferrer">
                    Book a Workshop
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop"
                alt="Branding workshop"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Image */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1200&h=675&fit=crop"
              alt="Strategic Success"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">
            Book Your Marketing Audit Now
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Get a free marketing audit and discover opportunities for growth.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-[#FBD116] hover:bg-[#E5C015] text-gray-900 font-semibold text-lg px-8"
          >
            <a href="https://calendly.com/bronny-mpremarketing/30min" target="_blank" rel="noopener noreferrer">
              Book Free Marketing Audit
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default MarketingStrategy;
